/**
 * i18n — single source of truth for UI language support.
 *
 * Supported UI languages (ISO 639-1):
 *   en  English    (LTR, default)
 *   ar  العربية    (RTL)
 *   fr  Français   (LTR)
 *   es  Español    (LTR)
 *   pt  Português  (LTR)
 *
 * To add a 6th language:
 *   1. Add a JSON file in src/i18n/locales/<code>.json with the same keys.
 *   2. Import it below and add it to `resources`.
 *   3. Add the entry to SUPPORTED_UI_LANGS.
 *   4. If RTL, add the code to RTL_LANGS.
 *   No other code changes needed — the switcher and persistence hooks read from
 *   SUPPORTED_UI_LANGS automatically.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import ar from "./locales/ar.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";

// ── Canonical language list ───────────────────────────────────────────────────
// One source of truth for the UI. The call-flow language list lives in
// src/i18n/callFlowLangs.js (separate concern — do NOT merge).
export const SUPPORTED_UI_LANGS = [
  { code: "en", label: "English",    dir: "ltr" },
  { code: "ar", label: "العربية",    dir: "rtl" },
  { code: "fr", label: "Français",   dir: "ltr" },
  { code: "es", label: "Español",    dir: "ltr" },
  { code: "pt", label: "Português",  dir: "ltr" },
];

export const RTL_LANGS = new Set(["ar"]);

/** True if the given ISO 639-1 code is RTL */
export const isRtlLang = (code) => RTL_LANGS.has(code);

// ── i18next init ──────────────────────────────────────────────────────────────
// Namespace strategy: one flat namespace per domain ("nav", "common", "auth", …)
// inside each locale file. Each component pulls what it needs:
//   const { t } = useTranslation(["nav","common"]);
//   t("nav:dashboard")  or  t("nav.dashboard")

// Pack all domain keys into a single resource object per language.
// New namespaces can be added to any locale file without changing this file.
function makeResources(...pairs) {
  const out = {};
  for (const [code, catalog] of pairs) {
    out[code] = {};
    for (const [ns, keys] of Object.entries(catalog)) {
      out[code][ns] = keys;
    }
  }
  return out;
}

const resources = makeResources(
  ["en", en],
  ["ar", ar],
  ["fr", fr],
  ["es", es],
  ["pt", pt],
);

// Resolve initial language: localStorage > browser > default "en"
const storedLang = localStorage.getItem("teleredo_ui_lang");
const validCodes  = SUPPORTED_UI_LANGS.map((l) => l.code);
const initialLang = (storedLang && validCodes.includes(storedLang))
  ? storedLang
  : (validCodes.includes(navigator.language?.slice(0, 2))
    ? navigator.language.slice(0, 2)
    : "en");

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng:        initialLang,
    fallbackLng: "en",
    ns:         ["nav", "common", "auth", "dashboard", "profile", "callflow", "agents", "analytics", "billing", "errors"],
    defaultNS:  "common",
    interpolation: { escapeValue: false },
    // Keep the existing missing-key behaviour silent in prod
    saveMissing: false,
    // Debug only in dev
    debug: import.meta.env.DEV ? false : false,
  });

// ── Document dir + lang attribute ────────────────────────────────────────────
function applyLangToDocument(code) {
  const dir = isRtlLang(code) ? "rtl" : "ltr";
  document.documentElement.dir  = dir;
  document.documentElement.lang = code;
  document.body.dir             = dir;
  // Persist to localStorage (unauthenticated fallback)
  localStorage.setItem("teleredo_ui_lang", code);
}

// Apply immediately on load
applyLangToDocument(initialLang);

// Apply on every subsequent change
i18n.on("languageChanged", applyLangToDocument);

export default i18n;
